// src/pages/motolivraison/commande-detail.page.jsx
//
// Détail d'une commande avec actions admin : attribution manuelle livreur, annulation.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Descriptions, Table, Modal, Form, Select, Input, Tag,
  Button, message, Space, Breadcrumb, Spin, Steps, Avatar, Typography, Alert,
  Timeline, Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined, RightOutlined, UserAddOutlined, CloseCircleOutlined,
  ShopOutlined, UserOutlined, PhoneOutlined, EnvironmentOutlined,
  ShoppingCartOutlined, DollarOutlined, ClockCircleOutlined, MobileOutlined,
  KeyOutlined, CheckCircleOutlined, InfoCircleOutlined, ForwardOutlined,
  HistoryOutlined, UserOutlined as UserIcon,
} from '@ant-design/icons';
import { useParams, useNavigate, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { motolivraisonService } from '../../common/services';
import { formatPhoneCI } from '../../common/helper';
import { LOCALE_TABLE, BTN_ACTION, COULEURS_ACTION } from './table.helpers';
import './css/motolivraison-table.css';

const { Text } = Typography;
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
const fmtF = (n) => `${fmt(n)} F`;

// Couleur des points du journal des changements
const STATUT_COULEUR_POINT = {
  annulee: 'red',
  livree: 'green',
  confirmee: 'green',
  soldee: 'green',
};

// Couleur d'ambiance de la bannière selon l'état de la commande
const COULEUR_STATUT = {
  en_attente_paiement: '#faad14',
  payee: '#13c2c2',
  en_attente_livreur: '#fa8c16',
  assignee: '#1677ff',
  recuperee: '#722ed1',
  en_livraison: '#2f54eb',
  livree: '#52c41a',
  confirmee: '#52c41a',
  soldee: '#389e0d',
  annulee: '#f5222d',
};

const STATUS_LABELS = {
  en_attente_paiement: 'Attente paiement',
  payee: 'Payée',
  en_attente_livreur: 'Attente livreur',
  assignee: 'Assignée',
  recuperee: 'Récupérée',
  en_livraison: 'En livraison',
  livree: 'Livrée',
  confirmee: 'Confirmée',
  soldee: 'Soldée',
  annulee: 'Annulée',
};

const TIMELINE_STEPS = [
  'payee', 'en_attente_livreur', 'assignee', 'recuperee', 'en_livraison', 'livree', 'confirmee', 'soldee'
];

const CommandeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [commande, setCommande] = useState(null);
  const [loading, setLoading] = useState(true);
  const [livreurs, setLivreurs] = useState([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [assignForm] = Form.useForm();
  const [cancelForm] = Form.useForm();
  // Avancement manuel (commande bloquée)
  const [avancerModalOpen, setAvancerModalOpen] = useState(false);
  const [avancerForm] = Form.useForm();
  const [avancement, setAvancement] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.getCommandeById(id);
      setCommande(data);
    } catch (e) {
      message.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const openAssign = async () => {
    try {
      const data = await motolivraisonService.listLivreurs({ status: 'approved' });
      setLivreurs(data?.livreurs || []);
      setAssignModalOpen(true);
    } catch (e) {
      message.error('Erreur lors du chargement des livreurs');
    }
  };

  const handleAssign = async () => {
    try {
      const { livreurId } = await assignForm.validateFields();
      await motolivraisonService.assignerLivreur(id, livreurId);
      message.success('Livreur assigné');
      setAssignModalOpen(false);
      fetch();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur');
    }
  };

  /// Débloque une commande figée. Le motif est obligatoire : c'est lui qui
  /// documentera l'intervention dans l'historique.
  const handleAvancer = async () => {
    try {
      const values = await avancerForm.validateFields();
      setAvancement(true);
      const res = await motolivraisonService.avancerCommande(id, {
        statut: values.statut || undefined,
        motif: values.motif.trim(),
      });
      message.success(res?.message || 'Commande avancée');
      setAvancerModalOpen(false);
      avancerForm.resetFields();
      fetch();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || "Impossible de faire avancer la commande");
    } finally {
      setAvancement(false);
    }
  };

  const handleCancel = async () => {
    try {
      const { reason } = await cancelForm.validateFields();
      await motolivraisonService.cancelCommande(id, reason);
      message.success('Commande annulée');
      setCancelModalOpen(false);
      fetch();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur');
    }
  };

  if (loading || !commande) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }

  const currentStepIdx = TIMELINE_STEPS.indexOf(commande.statut);
  const couleurStatut = COULEUR_STATUT[commande.statut] || '#475569';

  // Étapes qu'un admin peut forcer (miroir du service serveur). « Assignée »
  // en est absente : elle exige de désigner un livreur, via l'attribution.
  const ETAPES_FORCABLES = ['en_attente_livreur', 'recuperee', 'en_livraison', 'livree', 'confirmee'];
  const ORDRE_ETAPES = ['payee', 'en_attente_livreur', 'assignee', 'recuperee', 'en_livraison', 'livree', 'confirmee'];
  const rangActuel = ORDRE_ETAPES.indexOf(commande.statut);
  const etapesPossibles = rangActuel === -1
    ? []
    : ETAPES_FORCABLES.filter(e => ORDRE_ETAPES.indexOf(e) > rangActuel);
  const prochaineEtape = etapesPossibles[0] || null;
  const peutAvancer = !['annulee', 'soldee'].includes(commande.statut) && !!prochaineEtape;
  // Trois façons de payer à la livraison : QR Wave, espèces, ou mode téléphone
  // (le commerçant saisit la commande, le client règle au livreur). Oublier
  // « especes » affichait « Prépayée » sur des commandes payées en liquide.
  const paiementALaLivraison = commande.mode === 'telephone'
    || ['wave_livraison', 'especes'].includes(commande.modePaiement);
  const nbArticles = (commande.articles || []).reduce((t, a) => t + Number(a.quantite || 0), 0);
  // Horodatage de chaque étape, affiché sous le libellé du suivi
  const DATES_ETAPES = {
    payee: commande.payeeAt,
    en_attente_livreur: commande.dispatcheeAt,
    assignee: commande.assigneeAt,
    recuperee: commande.recupereeAt,
    en_livraison: commande.enLivraisonAt,
    livree: commande.livreeAt,
    confirmee: commande.confirmeParClientAt || commande.autoConfirmeAt,
    soldee: commande.soldeeAt,
  };
  const isCancelled = commande.statut === 'annulee';
  const canAssign = ['en_attente_livreur', 'payee'].includes(commande.statut);
  const canCancel = !['confirmee', 'soldee', 'annulee'].includes(commande.statut);

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item><Link to="/dashboard/motolivraison/commandes">Commandes</Link></Breadcrumb.Item>
          <Breadcrumb.Item>{id.slice(-8)}</Breadcrumb.Item>
        </Breadcrumb>

        {/* ─── Bannière : la commande en un coup d'œil ─────────────────── */}
        <Card style={{ marginBottom: 16, overflow: 'hidden' }} styles={{ body: { padding: 0 } }}>
          <div
            style={{
              padding: '22px 24px',
              background: `linear-gradient(135deg, #334155, ${couleurStatut})`,
              color: '#fff',
            }}
          >
            <Row gutter={[16, 16]} align="middle" justify="space-between" wrap>
              <Col xs={24} lg={15}>
                <Space size={18} align="start" wrap={false}>
                  <span
                    style={{
                      width: 64, height: 64, borderRadius: 16, flexShrink: 0,
                      background: 'rgba(255,255,255,.18)', border: '2px solid rgba(255,255,255,.5)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26,
                    }}
                  >
                    <ShoppingCartOutlined />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <h2 style={{ color: '#fff', margin: 0, fontSize: 24, lineHeight: 1.2 }}>
                      Commande #{String(commande._id).slice(-6).toUpperCase()}
                    </h2>
                    <Space size={[6, 6]} wrap style={{ marginTop: 8 }}>
                      <Tag color={isCancelled ? 'red' : 'blue'} style={{ margin: 0 }}>
                        {STATUS_LABELS[commande.statut] || commande.statut}
                      </Tag>
                      <Tag style={{ margin: 0 }} icon={commande.mode === 'app' ? <MobileOutlined /> : <PhoneOutlined />}>
                        {commande.mode === 'app' ? 'Commande dans l\'app' : 'Commande par téléphone'}
                      </Tag>
                      <Tag color={paiementALaLivraison ? 'orange' : 'green'} style={{ margin: 0 }}>
                        {commande.modePaiement === 'especes'
                          ? 'Espèces à la livraison'
                          : paiementALaLivraison ? 'Wave à la livraison' : 'Prépayée'}
                      </Tag>
                    </Space>
                    <div style={{ marginTop: 10, fontSize: 13, opacity: 0.92 }}>
                      <ClockCircleOutlined style={{ marginRight: 6 }} />
                      Créée le {dayjs(commande.createdAt).format('DD/MM/YYYY à HH:mm')}
                      {commande.livreeAt && (
                        <span style={{ marginLeft: 14 }}>
                          <CheckCircleOutlined style={{ marginRight: 6 }} />
                          Livrée le {dayjs(commande.livreeAt).format('DD/MM/YYYY à HH:mm')}
                        </span>
                      )}
                    </div>
                  </div>
                </Space>
              </Col>

              <Col xs={24} lg={9} style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, opacity: 0.85 }}>Total payé par le client</div>
                <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.2 }}>{fmtF(commande.montantTTC)}</div>
                <Space wrap style={{ justifyContent: 'flex-end', marginTop: 10 }}>
                  <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Retour</Button>
                  {canAssign && (
                    <Button icon={<UserAddOutlined />} style={BTN_ACTION(COULEURS_ACTION.voir)} onClick={openAssign}>
                      Attribuer un livreur
                    </Button>
                  )}
                  {peutAvancer && (
                    <Tooltip title={`Débloquer la commande — prochaine étape : ${STATUS_LABELS[prochaineEtape]}`}>
                      <Button
                        icon={<ForwardOutlined />}
                        style={BTN_ACTION('#722ed1')}
                        onClick={() => {
                          avancerForm.resetFields();
                          avancerForm.setFieldsValue({ statut: prochaineEtape });
                          setAvancerModalOpen(true);
                        }}
                      >
                        Faire avancer
                      </Button>
                    </Tooltip>
                  )}
                  {canCancel && (
                    <Button icon={<CloseCircleOutlined />} style={BTN_ACTION(COULEURS_ACTION.danger)}
                      onClick={() => setCancelModalOpen(true)}>
                      Annuler
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>
          </div>

          {/* Décomposition du montant, directement sous la bannière */}
          <Row style={{ borderTop: '1px solid #f0f0f0' }}>
            {[
              { label: 'Articles (revient au commerçant)', valeur: fmtF(commande.montantArticles), couleur: '#334155', icone: <ShopOutlined /> },
              { label: 'Commission TIGOON', valeur: fmtF(commande.commissionMontant), couleur: '#722ed1', icone: <DollarOutlined /> },
              { label: 'Total client', valeur: fmtF(commande.montantTTC), couleur: '#fa8c16', icone: <DollarOutlined /> },
              { label: 'Articles commandés', valeur: `${fmt(nbArticles)} article${nbArticles > 1 ? 's' : ''}`, couleur: '#1677ff', icone: <ShoppingCartOutlined /> },
            ].map((k, i) => (
              <Col xs={12} md={6} key={k.label} style={{ padding: '14px 18px', borderLeft: i ? '1px solid #f0f0f0' : 'none' }}>
                <Space size={10} align="center">
                  <span style={{ background: `${k.couleur}1a`, color: k.couleur, width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {k.icone}
                  </span>
                  <div style={{ lineHeight: 1.25 }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{k.label}</div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: k.couleur }}>{k.valeur}</div>
                  </div>
                </Space>
              </Col>
            ))}
          </Row>
        </Card>

        {/* ─── Suivi ──────────────────────────────────────────────────── */}
        <Card size="small" title="Suivi de la commande" style={{ marginBottom: 16 }}>
          {isCancelled ? (
            <Alert
              type="error"
              showIcon
              message="Commande annulée"
              description={commande.raisonAnnulation || 'Aucun motif enregistré.'}
            />
          ) : (
            <Steps
              current={currentStepIdx >= 0 ? currentStepIdx : 0}
              size="small"
              responsive
              items={TIMELINE_STEPS.map(st => ({
                title: STATUS_LABELS[st],
                description: DATES_ETAPES[st] ? dayjs(DATES_ETAPES[st]).format('DD/MM HH:mm') : undefined,
              }))}
            />
          )}
        </Card>

        <Row gutter={[16, 16]}>
          {/* ─── Colonne principale : contenu de la commande ───────────── */}
          <Col xs={24} xl={16}>
            <Card size="small" title={<Space><ShoppingCartOutlined />Articles commandés</Space>} style={{ marginBottom: 16 }}>
              <Table
                className="moto-table"
                size="middle"
                pagination={false}
                locale={LOCALE_TABLE}
                dataSource={(commande.articles || []).map((a, i) => ({ ...a, key: i }))}
                scroll={{ x: 520 }}
                columns={[
                  { title: 'Article', dataIndex: 'nom', ellipsis: true },
                  { title: 'Prix unitaire', dataIndex: 'prixUnitaire', align: 'right', width: 130, render: v => fmtF(v) },
                  { title: 'Quantité', dataIndex: 'quantite', align: 'center', width: 100 },
                  { title: 'Sous-total', dataIndex: 'sousTotal', align: 'right', width: 130, render: v => <strong>{fmtF(v)}</strong> },
                ]}
                summary={() => (
                  <Table.Summary>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={3} align="right">Articles</Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">{fmtF(commande.montantArticles)}</Table.Summary.Cell>
                    </Table.Summary.Row>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={3} align="right">Commission TIGOON</Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <span style={{ color: '#722ed1' }}>{fmtF(commande.commissionMontant)}</span>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={3} align="right"><strong>Total client</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <strong style={{ fontSize: 16, color: '#fa8c16' }}>{fmtF(commande.montantTTC)}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            </Card>

            {commande.codeSMSConfirmation && (
              <Card size="small" title={<Space><KeyOutlined />Code de confirmation de livraison</Space>} style={{ marginBottom: 16 }}>
                <div style={{
                  fontSize: 30, fontWeight: 700, textAlign: 'center', letterSpacing: 10,
                  color: '#fa8c16', background: '#fff7e6', borderRadius: 10, padding: '14px 0',
                }}>
                  {commande.codeSMSConfirmation}
                </div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8, textAlign: 'center' }}>
                  Le client communique ce code au livreur pour valider la remise.
                </Text>
              </Card>
            )}

            <Card
              size="small"
              title={<Space><HistoryOutlined />Journal des changements</Space>}
              style={{ marginBottom: 16 }}
            >
              {(commande.statutHistorique || []).length === 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>Aucun historique enregistré.</Text>
              ) : (
                <Timeline
                  items={[...(commande.statutHistorique || [])].reverse().map((h) => {
                    const auteur = h.parUserId
                      ? `${h.parUserId.firstName || ''} ${h.parUserId.lastName || ''}`.trim()
                      : null;
                    const force = (h.commentaire || '').startsWith('⚙️');
                    return {
                      color: force ? '#722ed1' : (STATUT_COULEUR_POINT[h.statut] || 'blue'),
                      dot: force ? <ForwardOutlined style={{ fontSize: 15 }} /> : undefined,
                      children: (
                        <div style={{ paddingBottom: 4 }}>
                          <Space size={8} wrap>
                            <Tag color={force ? 'purple' : undefined}>
                              {STATUS_LABELS[h.statut] || h.statut}
                            </Tag>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {h.date ? dayjs(h.date).format('DD/MM/YYYY HH:mm') : ''}
                            </Text>
                          </Space>
                          {h.commentaire && (
                            <div style={{ fontSize: 12.5, marginTop: 2 }}>{h.commentaire}</div>
                          )}
                          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
                            <UserIcon style={{ marginRight: 5 }} />
                            {auteur
                              ? `${auteur}${h.parUserId?.role ? ` (${h.parUserId.role})` : ''}`
                              : 'Système'}
                          </div>
                        </div>
                      ),
                    };
                  })}
                />
              )}
            </Card>

            <Card size="small" title={<Space><InfoCircleOutlined />Chronologie & références</Space>}>
              <Descriptions column={{ xs: 1, sm: 2 }} size="small" colon={false} labelStyle={{ color: '#8c8c8c' }}>
                <Descriptions.Item label="Référence complète"><Text code>{commande._id}</Text></Descriptions.Item>
                <Descriptions.Item label="Mode de commande">
                  {commande.mode === 'app' ? 'Application client' : 'Saisie par le commerçant (téléphone)'}
                </Descriptions.Item>
                <Descriptions.Item label="Mode de paiement">
                  {commande.modePaiement === 'especes'
                    ? 'Espèces remises au livreur'
                    : paiementALaLivraison ? 'Wave à la livraison (QR)' : 'Wave prépayé'}
                </Descriptions.Item>
                <Descriptions.Item label="Créée le">{dayjs(commande.createdAt).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>
                {commande.payeeAt && <Descriptions.Item label="Payée le">{dayjs(commande.payeeAt).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>}
                {commande.assigneeAt && <Descriptions.Item label="Assignée le">{dayjs(commande.assigneeAt).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>}
                {commande.recupereeAt && <Descriptions.Item label="Récupérée le">{dayjs(commande.recupereeAt).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>}
                {commande.enLivraisonAt && <Descriptions.Item label="En livraison le">{dayjs(commande.enLivraisonAt).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>}
                {commande.livreeAt && <Descriptions.Item label="Livrée le">{dayjs(commande.livreeAt).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>}
                {(commande.confirmeParClientAt || commande.autoConfirmeAt) && (
                  <Descriptions.Item label="Confirmée le">
                    {dayjs(commande.confirmeParClientAt || commande.autoConfirmeAt).format('DD/MM/YYYY HH:mm')}
                    {!commande.confirmeParClientAt && ' (auto)'}
                  </Descriptions.Item>
                )}
                {commande.soldeeAt && <Descriptions.Item label="Reversée le">{dayjs(commande.soldeeAt).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>}
                {commande.paiementLivraisonRecuAt && (
                  <Descriptions.Item label="Paiement reçu le">
                    {dayjs(commande.paiementLivraisonRecuAt).format('DD/MM/YYYY HH:mm')}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          </Col>

          {/* ─── Colonne latérale : les acteurs ────────────────────────── */}
          <Col xs={24} xl={8}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Card size="small" title={<Space><ShopOutlined />Boutique</Space>}>
                <Space align="start" size={12}>
                  <Avatar size={44} src={commande.commerceId?.logo} icon={<ShopOutlined />} style={{ backgroundColor: '#fa8c16' }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{commande.commerceId?.nom || '—'}</div>
                    {commande.commerceId?._id && (
                      <Button
                        type="link" size="small" style={{ paddingLeft: 0 }}
                        onClick={() => navigate(`/dashboard/motolivraison/commerces/${commande.commerceId._id}`)}
                      >
                        Voir la boutique →
                      </Button>
                    )}
                  </div>
                </Space>
              </Card>

              <Card size="small" title={<Space><UserOutlined />Client</Space>}>
                <div style={{ fontWeight: 600 }}>{commande.clientNom || '—'}</div>
                <div style={{ fontSize: 13, color: '#595959', marginTop: 2 }}>
                  <PhoneOutlined style={{ marginRight: 6 }} />{formatPhoneCI(commande.clientPhone || commande.clientId?.phone)}
                </div>
                <div style={{ fontSize: 13, color: '#595959', marginTop: 6 }}>
                  <EnvironmentOutlined style={{ marginRight: 6 }} />
                  {commande.adresseLivraison?.libelle || '—'}
                </div>
                {commande.adresseLivraison?.quartier && (
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 20 }}>
                    Quartier : {commande.adresseLivraison.quartier}
                  </div>
                )}
                {commande.adresseLivraison?.latitude != null && commande.adresseLivraison?.longitude != null ? (
                  <Button
                    block icon={<EnvironmentOutlined />} style={{ marginTop: 10 }}
                    href={`https://www.google.com/maps/search/?api=1&query=${commande.adresseLivraison.latitude},${commande.adresseLivraison.longitude}`}
                    target="_blank" rel="noreferrer"
                  >
                    Ouvrir dans Google Maps
                  </Button>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                    Pas de position GPS : le livreur n'a que l'adresse texte.
                  </Text>
                )}
              </Card>

              <Card size="small" title={<Space><UserOutlined />Livreur</Space>}>
                {commande.livreurId ? (
                  <>
                    <Space align="start" size={12}>
                      <Avatar size={44} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
                      <div>
                        <div style={{ fontWeight: 600 }}>
                          {`${commande.livreurId.firstName || ''} ${commande.livreurId.lastName || ''}`.trim()}
                        </div>
                        <div style={{ fontSize: 13, color: '#595959' }}>{formatPhoneCI(commande.livreurId.phone)}</div>
                        <Button
                          type="link" size="small" style={{ paddingLeft: 0 }}
                          onClick={() => navigate(`/dashboard/motolivraison/livreurs/${commande.livreurId._id}`)}
                        >
                          Voir sa fiche →
                        </Button>
                      </div>
                    </Space>
                    <Button block icon={<PhoneOutlined />} style={{ marginTop: 10 }} href={`tel:${commande.livreurId.phone}`}>
                      Appeler le livreur
                    </Button>
                  </>
                ) : (
                  <>
                    <Text type="secondary" style={{ fontSize: 13 }}>Aucun livreur assigné.</Text>
                    {canAssign && (
                      <Button
                        block icon={<UserAddOutlined />} style={{ ...BTN_ACTION(COULEURS_ACTION.voir), marginTop: 10 }}
                        onClick={openAssign}
                      >
                        Attribuer manuellement
                      </Button>
                    )}
                  </>
                )}
              </Card>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Modal Attribution livreur */}
      <Modal
        title="Attribuer manuellement un livreur"
        open={assignModalOpen}
        onCancel={() => setAssignModalOpen(false)}
        onOk={handleAssign}
        okText="Attribuer"
        cancelText="Annuler"
      >
        <Form form={assignForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="livreurId"
            label="Livreur"
            rules={[{ required: true, message: 'Veuillez choisir un livreur' }]}
          >
            <Select
              placeholder="Sélectionner un livreur approuvé"
              showSearch
              optionFilterProp="label"
              options={livreurs.map(l => ({
                value: l._id,
                label: `${l.firstName} ${l.lastName} — ${l.phone}${l.livreurMotoProfile?.isAvailable ? ' 🟢' : ''}`,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal : faire avancer une commande bloquée */}
      <Modal
        title={<span><ForwardOutlined style={{ marginRight: 8, color: '#722ed1' }} />Faire avancer la commande</span>}
        open={avancerModalOpen}
        onCancel={() => setAvancerModalOpen(false)}
        onOk={handleAvancer}
        okText="Confirmer l'avancement"
        cancelText="Annuler"
        confirmLoading={avancement}
        width={560}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Intervention manuelle"
          description={
            <span style={{ fontSize: 13 }}>
              À réserver aux commandes bloquées (livreur injoignable, application figée,
              paiement jamais notifié). L'étape déclenche les mêmes effets que le parcours
              normal — règlement du gage espèces, gains du livreur, stocks — et
              <strong> votre nom sera inscrit dans l'historique de la commande</strong>.
            </span>
          }
        />
        <Form form={avancerForm} layout="vertical">
          <Form.Item
            name="statut"
            label="Nouvelle étape"
            rules={[{ required: true, message: 'Choisissez une étape' }]}
            extra="On ne peut qu'avancer : revenir en arrière fausserait les compteurs déjà incrémentés."
          >
            <Select
              options={etapesPossibles.map(e => ({
                value: e,
                label: e === prochaineEtape
                  ? `${STATUS_LABELS[e]} (étape suivante)`
                  : STATUS_LABELS[e],
              }))}
            />
          </Form.Item>
          <Form.Item
            name="motif"
            label="Motif de l'intervention"
            rules={[
              { required: true, message: 'Le motif est obligatoire' },
              { min: 5, message: 'Soyez un peu plus précis (5 caractères minimum)' },
            ]}
          >
            <Input.TextArea
              rows={3}
              maxLength={200}
              showCount
              placeholder="Ex : livreur injoignable depuis 30 min, client confirme avoir reçu la commande par téléphone"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Annulation */}
      <Modal
        title="Annuler la commande"
        open={cancelModalOpen}
        onCancel={() => setCancelModalOpen(false)}
        onOk={handleCancel}
        okText="Confirmer l'annulation"
        cancelText="Retour"
        okButtonProps={{ danger: true }}
      >
        <Form form={cancelForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="reason"
            label="Raison de l'annulation"
            rules={[{ required: true, message: 'La raison est requise' }]}
          >
            <Input.TextArea rows={3} placeholder="Pourquoi annulez-vous cette commande ?" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CommandeDetail;
